<template>
    <div>
        <!-- Quality Defect list -->
        <q-table
            v-if="!isSelectMode"
            @request="onRequest"
            :filter="table.search"
            :rows="qualityDefects"
            :columns="table.cols"
            v-model:pagination="table.pagination"
            :rows-per-page-options="[table.pagination.rowsPerPage]"
            :no-data-label="$t('No quality defect available')"
            :class="{ 'sticky-table quality-defects-table': $q.screen.gt.lg }"
            row-key="id"
            binary-state-sort
            flat
            :grid="$q.screen.lt.lg"
        >
            <!-- search bar -->
            <template v-slot:top>
                <div class="col-12 text-h5 text-weight-medium">
                    {{
                        $t('Quality Defects for {asset}', { asset: asset.name })
                    }}
                </div>
                <div class="page-description">
                    {{
                        $t('Manage all quality defects related to this asset.')
                    }}
                </div>
                <div
                    class="row fit q-mb-sm"
                    :class="{
                        'wrap justify-between items-start content-start':
                            $q.screen.lt.sm
                    }"
                >
                    <q-input
                        data-test="search-quality-defect-input"
                        v-model="table.search"
                        @clear="table.search = ''"
                        :placeholder="$t('Search quality defect')"
                        :class="
                            $q.screen.lt.sm ? 'col-12 q-mb-md' : 'col q-mr-md'
                        "
                        color="accent"
                        debounce="750"
                        clearable
                        outlined
                        dense
                    >
                        <template v-slot:prepend>
                            <q-icon name="search" />
                        </template>
                    </q-input>
                    <q-btn
                        id="add-quality-defect-button"
                        @click="isSelectMode = true"
                        :label="$t('Add quality defect')"
                        :disable="$userCannot('edit', 'assets')"
                        class="table-header__button"
                        :class="{ 'col-12': $q.screen.lt.sm }"
                        color="accent"
                        size="md"
                    />
                </div>
            </template>

            <!-- actions column -->
            <template v-slot:body-cell-actions="props">
                <q-td :props="props">
                    <div class="row q-gutter-md">
                        <q-btn
                            @click="
                                $router.push({
                                    name: 'quality_defect_update',
                                    params: {
                                        facilityId: facilityId,
                                        qualityDefectId: props.row.id
                                    },
                                    state: {
                                        name: props.row.name,
                                        code: props.row.code,
                                        description: props.row.description
                                    }
                                })
                            "
                            :disable="$userCannot('edit', 'quality_defects')"
                            class="table__action-button"
                            color="accent"
                            icon="fas fa-edit"
                            outline
                        >
                            <q-tooltip>
                                {{ $t('Edit quality defect') }}
                            </q-tooltip>
                        </q-btn>
                        <q-btn
                            id="remove-quality-defect-button"
                            @click="remove(props.row)"
                            :disable="$userCannot('delete', 'quality_defects')"
                            :loading="props.row.isLoading"
                            class="table__action-button"
                            color="negative"
                            icon="fas fa-trash-alt"
                            outline
                        >
                            <template v-slot:loading>
                                <q-spinner />
                            </template>
                            <q-tooltip>{{
                                $t('Remove quality defect')
                            }}</q-tooltip>
                        </q-btn>
                    </div>
                </q-td>
            </template>

            <!-- No quality defects -->
            <template v-slot:no-data>
                <span class="text-weight-regular">
                    {{
                        `${$t('No quality defects are linked to this asset.')}.`
                    }}
                    <q-btn
                        @click="isSelectMode = true"
                        :label="$t('Click here to add some.')"
                        class="inline-button"
                        color="accent"
                        dense
                        flat
                        no-caps
                    />
                </span>
            </template>

            <!-- card template for small devices -->
            <template v-slot:item="props">
                <defect-card
                    @edit="
                        $router.push({
                            name: 'quality_defect_update',
                            params: {
                                facilityId: facilityId,
                                qualityDefectId: props.row.id
                            },
                            state: {
                                name: props.row.name,
                                code: props.row.code,
                                description: props.row.description
                            }
                        })
                    "
                    @delete="remove(props.row)"
                    :name="props.row.name"
                    :description="props.row.description"
                    :code="props.row.code"
                />
            </template>
        </q-table>

        <!-- Available Quality Defect list -->
        <q-table
            v-else
            @request="onRequest"
            :filter="table.search"
            :rows="qualityDefects"
            :columns="table.cols"
            v-model:pagination="table.pagination"
            :rows-per-page-options="[table.pagination.rowsPerPage]"
            v-model:selected="table.selected"
            class="sticky-table quality-defects-table q-ma-none"
            table-style="max-height: none;"
            selection="multiple"
            row-key="id"
            binary-state-sort
            :no-data-label="$t('No quality defect available')"
            flat
        >
            <!-- search bar -->
            <template v-slot:top>
                <div class="col-12 text-h6 q-mb-md">
                    <q-btn
                        @click="confirmReturn"
                        :label="$t('Return to asset quality defects')"
                        icon="fas fa-chevron-left"
                        color="primary"
                        flat
                    />
                </div>

                <div class="col-12 text-h6 q-mb-md">
                    {{ $t('Select quality defects for this asset') }}
                </div>
                <div
                    class="fit row wrap justify-between items-start content-start"
                >
                    <q-input
                        data-test="search-quality-defect-input"
                        v-model="table.search"
                        @clear="table.search = ''"
                        :placeholder="$t('Search quality defect')"
                        class="col q-mr-md"
                        type="string"
                        debounce="750"
                        clearable
                        outlined
                        dense
                        color="accent"
                    >
                        <template v-slot:prepend>
                            <q-icon name="search" />
                        </template>
                    </q-input>
                    <div class="q-mb-md">
                        <q-btn
                            id="confirm-selection-button"
                            @click="confirmSelect"
                            :label="$t('Confirm selection')"
                            class="table-header__button"
                            color="positive"
                            size="md"
                        />
                    </div>
                </div>
            </template>
        </q-table>
    </div>
</template>

<script>
import DefectCard from '@components/DefectCard.vue';
import UpdateURL from '@mixins/UpdateURL.vue';
import { formatQualityDefects } from '../../utils/qualityDefects.js';
import { mapActions } from 'pinia';
import { useSharedStore } from '@stores/shared.js';

export default {
    name: 'AssetQualityDefectList',
    components: { DefectCard },
    mixins: [UpdateURL],
    props: {
        asset: {
            type: [Object],
            required: true
        },
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    watch: {
        isSelectMode: {
            handler() {
                // display list of all the quality defects
                this.table.search = '';
                this.initQualityDefectList();
            }
        }
    },
    data() {
        return {
            qualityDefects: [],
            isSelectMode: false,
            table: {
                cols: [
                    {
                        align: 'left',
                        field: 'name',
                        name: 'name',
                        label: this.$t('Name'),
                        sortable: true,
                        style:
                            // wrap text overflow to next line
                            'max-width: 150px; white-space: initial; word-wrap: break-word;'
                    },
                    {
                        align: 'left',
                        field: 'code',
                        name: 'code',
                        label: this.$t('Code'),
                        sortable: true,
                        style:
                            // wrap text overflow to next line
                            'max-width: 150px; white-space: initial; word-wrap: break-word;'
                    },
                    {
                        align: 'left',
                        field: 'description',
                        name: 'description',
                        label: this.$t('Description'),
                        style:
                            // wrap text overflow to next line
                            'max-width: 150px; white-space: initial; word-wrap: break-word;'
                    },
                    {
                        align: 'left',
                        field: 'actions',
                        name: 'actions',
                        label: this.$t('Actions')
                    }
                ],
                pagination: {
                    sortBy: '',
                    descending: false,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                },
                search: '',
                selected: []
            }
        };
    },
    created() {
        this.initQualityDefectList();
    },
    computed: {
        /**
         * Base URL to use to fetch quality defects.
         * According to the mode, the URL will be different.
         * @return {string}
         */
        baseURL() {
            if (this.isSelectMode) {
                return `assets/${this.asset.id}/available_defects/`;
            } else {
                return `assets/${this.asset.id}/defects/`;
            }
        },
        /**
         * Constructs a URL with query parameters for fetching defect data.
         *
         * @returns {String} - The constructed URL.
         *
         * The URL has the following structure:
         * /api/v1/assets/{assetId}/defects/?search={search}&ordering={ordering}&page={page}
         *
         * Query Parameters:
         * - search: (String) The search query to filter defects.
         * - ordering: (String) The sort order of the results.
         * - page: (Number) The page number for pagination.
         * - facility: (Number) The ID of the facility to filter defects by.
         */
        url() {
            // get current pagination state attributes
            let { page, sortBy, descending } = this.table.pagination;

            // update URL
            return this.updateURL({
                baseURL: this.baseURL,
                params: {
                    search: this.table.search,
                    ordering: descending ? `-${sortBy}` : `${sortBy}`,
                    page: page,
                    // add 'facility' in query if we are in select mode
                    ...(this.isSelectMode && {
                        facility: this.facilityId
                    })
                }
            });
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        /**
         * Get quality defects from the API.
         * @return {Object} - Quality defects.
         * The API response is an object with the following structure:
         * {
         *    count: 1,
         *    next: null,
         *    previous: null,
         *    results: [
         *      {
         *        id: 1,
         *        name: 'Quality defect 1',
         *        code: 'QD1',
         *        description: 'Quality defect 1 description'
         *      }
         *    ]
         * }
         */
        async getQualityDefects() {
            try {
                const response = await this.$axios.get(this.url);
                const formattedResponse = formatQualityDefects(
                    response.data.results
                );
                this.qualityDefects.push(...formattedResponse);

                // // update total items count
                this.table.pagination.rowsNumber = response.data.count;
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'Failed to load quality defects. Please try again later.'
                    ),
                    error
                );
            } finally {
                this.stopLoading();
            }
        },
        /**
         * Fetch data from the API. Triggered by any table sort/search/page changes.
         */
        async onRequest() {
            // begin loading
            this.beginLoading(`${this.$t('Loading quality defects')}...`);

            // // clear existing data
            this.qualityDefects = [];
            // fetch data from the API
            await this.getQualityDefects();
        },
        /**
         * Remove a quality defect for this asset.
         */
        async remove(row) {
            this.$confirmDelete(
                this.$t(
                    'Are you sure you want to remove {item} from {asset}?',
                    {
                        item: row.name,
                        asset: this.asset.name
                    }
                )
            ).onOk(async () => {
                try {
                    // begin loading
                    row.isLoading = true;
                    // unlink quality defect from asset
                    await this.$axios.post(
                        `assets/${this.asset.id}/remove_defect/`,
                        {
                            defect_ids: [row.id]
                        }
                    );
                    // remove quality defect from the list
                    this.qualityDefects = this.qualityDefects.filter(
                        qualityDefect => qualityDefect.id !== row.id
                    );

                    // decrement total row count
                    this.table.pagination.rowsNumber--;

                    // notify user
                    this.$notifySuccess(
                        this.$t('Removed quality defect from asset')
                    );
                } catch (error) {
                    this.$notifyError(
                        this.$t(
                            'Failed to remove quality defect. Please try again later.'
                        ),
                        error
                    );
                } finally {
                    row.isLoading = false;
                }
            });
        },
        /**
         * Confirm selected quality defects for this asset.
         */
        async confirmSelect() {
            // if no defects are selected, return
            if (!this.table.selected.length) {
                this.$notifyError(
                    this.$t('Please select at least one quality defect.')
                );
                return;
            }

            // extract ids from selected defects
            const defectIds = this.table.selected.map(defect => defect.id);

            try {
                await this.$axios.post(`assets/${this.asset.id}/add_defect/`, {
                    defect_ids: defectIds
                });
                this.$notifySuccess(
                    this.$t('Quality defects have been successfully added.')
                );
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'Failed to update quality defect. Please try again later.'
                    ),
                    error
                );
            } finally {
                // return to list view
                this.isSelectMode = false;
                // empty selected quality defects
                this.table.selected = [];
            }
        },
        /**
         * Cancel quality defects selection.
         */
        confirmReturn() {
            this.isSelectMode = false;
            this.table.selected = [];
        },

        /**
         * Initialize the quality defect list.
         */
        initQualityDefectList() {
            // load list of quality defects
            this.onRequest({
                pagination: this.table.pagination,
                filter: ''
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.quality-defects-table {
    height: 70vh;
}

.inline-button :deep(.q-btn__wrapper) {
    padding: 0px;
}

// align table top with cards
:deep(.q-table__top) {
    padding: 0px;
}
</style>
